import React, { useState } from 'react';
import { Edit3 } from 'lucide-react';

const PALE_DOT_CLASSES = [
  'bg-pink-200',
  'bg-indigo-200', 
  'bg-amber-200',
  'bg-emerald-200',
  'bg-blue-200',
  'bg-cyan-200',
  'bg-purple-200',
  'bg-yellow-200',
];

function parseDate(dateString) {
  if (!dateString) return { month: '', day: '', year: '' };
  const parts = dateString.split(' ');
  if (parts.length >= 3) {
    return {
      month: parts[0],
      day: parts[2],
      year: ''
    };
  }
  return { month: dateString, day: '', year: '' };
}

const TimelineDemo = () => {
  const [activeDemo, setActiveDemo] = useState(0);
  const [animatingEvents, setAnimatingEvents] = useState([]);
  const [typedText, setTypedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const demoQueries = [
    "Summarise next week's agenda - work and social",
    "Show me tomorrow's agenda",
    "What are my holiday plans for the next 6 months?",
    "What are my plans for the next few weekends?",
    "Book in a dinner at Sarah's on Saturday the 20th", 
    "When are my off-sites this year?",
    "I've booked to do the Boston marathon this year, suggest a training plan", 
    "Ok, book that in, and show me what next week's training looks like", 
    "Cancel the gym session with Jake and move the Tempo run to Thursday",
    "Show me my most important strategy meetings this quarter",
    "Summarize my quarterly product roadmap milestones",
    "Summarise my main wins for this year",
    "Organize my annual medical appointments and wellness checks",
    "Lay out my travel plans for upcoming conferences",
    "Chart my project deadlines for the rest of this month",
    "Summarize all dinners with investors for this year",
    "Stack my recurring 1:1s for next week",
    "Show my social commitments this weekend",
    "Build my annual off-site schedule",
    "Sequence my upcoming performance reviews by seniority",
    "Give me my networking events in descending order of size",
    "Bring up all deadlines for marketing campaigns this quarter",
    "Show me my most complex day next week",
    "Visualize all cross-team workshops for February",
    "Sequence marathon training and all social events together next week",
    "Display all NY-based client catchups in the next 2 months",
    "Stack my top priority deliverables for July",
    "Lay out my entire fitness training for the next four weeks",
    "Show all contract renewal dates for my direct reports",
    "Summarize all multi-step board approval processes",
    "Organize my next three speaking engagements",
    "Display my upcoming creative inspiration days",
    "Show me every project launch I'm leading this year",
    "Schedule my quarterly company town halls",
    "List my pitch meetings and follow-ups for next month",
    "Detail my upcoming international travel blocks",
    "Lay out all upcoming hiring panel sessions",
    "Visualize my non-work commitments for next Thursday",
    "Sum up my late-evening work sessions this week",
  ];

  const demoResults = [
    [
      { 
        title: "Weekly Kickoff & Marathon Training",
        dayName: "Mon",
        dayNumber: "7",
        monthName: "July",
        description:
          "Heavy day of back to back meetings\n" +
          "• 8:15am Team standup (Zoom)\n" +
          "• 10:00am 1:1 with CTO\n" +
          "• 12:00pm Marathon base run\n" +
          "• 7:30pm Dinner with Oliver (VC/Friends)"
      },
      {
        title: "Focus Day & Product Sync",
        dayName: "Tue",
        dayNumber: "8",
        monthName: "July",
        description:
          "Strategic planning, some downtime in evening\n" +
          "• 9:00am Deep work: Q3 planning\n" +
          "• 11:30am Stakeholder sync\n" +
          "• 6:00pm Drinks (Upper West) Bill & Tara"
      },
      {
        title: "Dept Head Meeting & Coaching",
        dayName: "Wed",
        dayNumber: "9",
        monthName: "July",
        description:
          "Long run, key leadership touchpoints\n" +
          "• 6:00am Tempo run (5 mi)\n" +
          "• 10:00am All-hands presentation\n" +
          "• 1:00pm Exec coaching\n" +
          "• 8:00pm Dinner at Eataly"
      },
      {
        title: "Midweek Reviews",
        dayName: "Thu",
        dayNumber: "10",
        monthName: "July",
        description:
          "Busy product reviews, social evening\n" +
          "• Back-to-back reviews\n" +
          "• 12:30pm Working lunch\n" +
          "• 7:30pm Team happy hour"
      },
      {
        title: "Board Prep & Long Run",
        dayName: "Fri",
        dayNumber: "11",
        monthName: "July",
        description:
          "Board materials, deadline focus, show in evening\n" +
          "• 6:30am Long run\n" +
          "• 9:00am Board prep\n" +
          "• 2:30pm Q2 report deadline\n" +
          "• 8:00pm Broadway tickets"
      },
      {
        title: "Long Run & Art Recharge",
        dayName: "Sat",
        dayNumber: "12",
        monthName: "July",
        description:
          "High-mileage workout, art and downtime\n" +
          "• 8:00am Marathon pace run (14mi)\n" +
          "• 6:30pm Gallery opening"
      },
      {
        title: "Planning & Family Check-in",
        dayName: "Sun",
        dayNumber: "13",
        monthName: "July",
        description:
          "Gentle recovery, plan next week\n" +
          "• 8:00am Recovery run\n" +
          "• 10:00am Weekly planning\n" +
          "• 1:00pm Facetime parents"
      }
    ],
    [
      {
        title: "Morning Strategy Session",
        startHour: "07:30",
        endHour: "08:30",
        dayName: "Wed",
        dayNumber: "9",
        monthName: "July",
        time: "7:30 AM",
        duration: "1 hr",
        description: "Deep work block: marathon training log + prepare for leadership sync",
        attendees: ["You", "Alex N.", "Riya S."]
      },
      {
        title: "Leadership Sync",
        startHour: "09:00",
        endHour: "10:00",
        dayName: "Wed",
        dayNumber: "9",
        monthName: "July",
        time: "9:00 AM",
        duration: "1 hr",
        description: "Weekly call with department heads - Q3 pipeline; review board deck",
        attendees: ["You", "Nina Patel", "Damon C.", "Maya Y."]
      },
      {
        title: "Executive Coaching",
        startHour: "11:00",
        endHour: "12:00",
        dayName: "Wed",
        dayNumber: "9",
        monthName: "July",
        time: "11:00 AM",
        duration: "1 hr",
        description: "Performance coaching (mid-year review & personal goals)",
        attendees: ["You", "Dr. Simmons"]
      },
      {
        title: "Client Lunch, Eleven Madison Park",
        startHour: "12:30",
        endHour: "14:00",
        dayName: "Wed",
        dayNumber: "9",
        monthName: "July",
        time: "12:30 PM",
        duration: "90 mins",
        description: "Lunch with prospective client—expansion plans & networking",
        attendees: ["You", "Olivia Forde", "Michael Y.", "+6 more"]
      },
      {
        title: "Evening Tempo Run, Central Park",
        startHour: "18:30",
        endHour: "19:20",
        dayName: "Wed",
        dayNumber: "9",
        monthName: "July",
        time: "6:30 PM",
        duration: "50 mins",
        description: "5-mile tempo run, meet club for cooldown and brief post-run drinks",
        attendees: ["You", "Jake P.", "Kerri S.", "+3 more"]
      },
    ],
    [
      {
        title: "Paris Getaway",
        startDate: "Fri May 24",
        endDate: "Tue May 28",
        time: "Flight 7:20 PM",
        duration: "5 days",
        description: "• Spring escape to Paris solo for food, culture, and walking miles.\n• Business class on Air France out of JFK Fri 7:20pm\n• Hôtel Grand Powers in the 8th\n• Fine dining: Septime, Le Comptoir du Relais, and street pâtisserie\n• Sunday: Orsay and Le Marais, rooftop sunset at Les Ombres"
      },
      { 
        title: "Summer Long Weekend in Jackson Hole",
        startDate: "Thu Jul 4",
        endDate: "Sun Jul 7",
        time: "Flight 9:00 AM",
        duration: "4 days",
        description: "• Independence Day hiking & luxury lodge experience.\n• Thursday: Morning flight to Jackson Hole, check-in Amangani\n• Hiking Grand Teton NP, spa retreat\n• Saturday: Stargazing, chef's tasting menu, unwind"
      },
      { 
        title: "Iceland Adventure",
        startDate: "Fri Sep 6",
        endDate: "Thu Sep 12",
        time: "Flight 5:15 PM",
        duration: "7 days",
        description: "• Active volcanoes, black-sand beaches, and city nights in Reykjavik.\n• Friday: Icelandair flight from JFK 5:15pm\n• Guided glacier trekking and northern lights chase\n• Sunday: Blue Lagoon spa\n• Remote cabin exploring the Golden Circle"
      },
      {
        title: "December Winter Escape in Mexico City",
        startDate: "Mon Dec 23",
        endDate: "Sat Dec 28",
        time: "Flight 11:25 AM",
        duration: "6 days",
        description: "• Warmth & culture for the holidays, solo.\n• Monday: JetBlue direct to CDMX\n• Polanco boutique hotel\n• Christmas Eve: Chef's table at Pujol\n• Art museums, biking Chapultepec, mezcal evenings"
      },
    ],
    [
      { 
        title: "Long Weekend in New York",
        startDate: "Thu Jul 4",
        endDate: "Mon Jul 8",
        time: "Flight 6:30 AM",
        duration: "4 days",
        description: "• NYC getaway with Emma for the July 4th long weekend.\n• Flight BA177 departs 6:30am Thursday\n• High Line Hotel, Chelsea\n• Saturday: Brooklyn Bridge, Hamilton 8pm"
      },
      { 
        title: "Executive Strategy Off-site",
        startDate: "Sat Jul 13",
        endDate: "Sun Jul 14",
        time: "9:00 AM",
        duration: "2 days",
        description: "• Annual leadership retreat with the senior team at Montauk Point Resort.\n• Saturday: Q3/Q4 planning, board dinner\n• Sunday: Team building, 2025 roadmap\n• Client retention strategy workshop"
      },
      { 
        title: "Carl's Farewell & Free Weekend",
        startDate: "Fri Jul 19",
        endDate: "Sun Jul 21",
        time: "12:30 PM",
        duration: "3 days",
        description: "• Farewell lunch for Carl who's moving to London, then a relaxing weekend.\n• Friday: Team lunch at Gramercy Tavern 12:30pm\n• Weekend: Hangover day, or marathon training?"
      },
    ],
    [
      {
        title: "Dinner at Sarah's",
        startHour: "19:00",
        endHour: "00:00", 
        dayName: "Sat",
        dayNumber: "20",
        monthName: "July",
        time: "7:00 PM",
        duration: "5 hours",
        description: "123 Maple Street, Chelsea"
      },
    ],
    [
      { 
        title: "Mid-Year Strategy Off-site",
        startDate: "Wed Jul 17",
        endDate: "Thu Jul 18",
        time: "9:00 AM",
        duration: "2 days",
        description: "• Half-year review and H2 planning session.\n• Wednesday: Performance review, market analysis\n• Thursday: Product roadmap, resource allocation\n• Location: Hudson Valley Conference Center"
      },
      { 
        title: "Sales Kickoff Retreat",
        startDate: "Mon Aug 26",
        endDate: "Tue Aug 27",
        time: "8:00 AM",
        duration: "2 days",
        description: "• Q3 sales strategy and team alignment.\n• Monday: Territory planning, quota setting\n• Tuesday: Product training, competitive intel\n• Lake George Resort, team building activities"
      },
      { 
        title: "Q4 Planning Retreat",
        startDate: "Fri Oct 4",
        endDate: "Sat Oct 5",
        time: "All day",
        duration: "2 days",
        description: "• Year-end strategy and 2026 kickoff planning.\n• Friday: Q4 priorities, holiday campaign\n• Saturday: 2026 budget, hiring roadmap\n• Vermont retreat center, overnight stay"
      },
      { 
        title: "Leadership Development",
        startDate: "Thu Nov 14",
        endDate: "Fri Nov 15",
        time: "9:00 AM",
        duration: "2 days",
        description: "• Executive coaching and leadership skills workshop.\n• Thursday: 360 feedback sessions, coaching\n• Friday: Change management, team dynamics\n• Napa Valley executive center"
      },
      { 
        title: "Year-End Board Retreat",
        startDate: "Mon Dec 16",
        endDate: "Tue Dec 17",
        time: "8:30 AM",
        duration: "2 days",
        description: "• Annual board meeting and strategic review.\n• Monday: Year review, financial performance\n• Tuesday: 2026 vision, board governance\n• Private club in the Berkshires"
      },
    ],
    [
      {
        title: "Base Building Phase",
        month: "Month 1",
        monthName: "Jul",
        year: "2025",
        time: "All week",
        duration: "4 weeks",
        description: "• Foundation phase focusing on building aerobic base.\n• 4-5 runs per week, 25-35 miles weekly"
      },
      {
        title: "Speed Work Introduction",
        month: "Month 2",
        monthName: "Aug",
        year: "2025",
        time: "Tues/Thurs",
        duration: "4 weeks",
        description: "• Adding structured speed work twice per week.\n• 5-6 runs per week, 35-45 miles weekly"
      },
      {
        title: "Peak Training Block",
        month: "Month 3",
        monthName: "Sep",
        year: "2025",
        time: "6 days/week",
        duration: "3 weeks",
        description: "• Highest intensity phase with back-to-back long runs.\n• 6 runs per week, 50-55 miles weekly"
      },
      {
        title: "Taper & Race Prep",
        month: "Month 4",
        monthName: "Oct",
        year: "2025",
        time: "Easy runs",
        duration: "3 weeks",
        description: "• Reducing volume while maintaining intensity.\n• 4-5 runs per week, 25-35 miles weekly"
      },
    ],
    [
      {
        title: "Gym Session with Jake",
        dayName: "Mon",
        dayNumber: "7",
        monthName: "July",
        time: "6:30 AM",
        duration: "45 mins",
        description: "Strength training session with Jake booked for 6:30am."
      },
      {
        title: "Rest Day",
        dayName: "Tue",
        dayNumber: "8",
        monthName: "July",
        time: "Rest",
        duration: "All day",
        description: "Complete rest or light stretching."
      },
      {
        title: "Tempo Run",
        dayName: "Wed",
        dayNumber: "9",
        monthName: "July",
        time: "6:00 AM",
        duration: "40 mins",
        description: "5-mile tempo run booked for 6:00am."
      },
      {
        title: "Rest Day",
        dayName: "Thu",
        dayNumber: "10",
        monthName: "July",
        time: "Rest",
        duration: "All day",
        description: "Complete rest or light stretching."
      },
      {
        title: "Easy Recovery Run",
        dayName: "Fri",
        dayNumber: "11",
        monthName: "July",
        time: "7:00 AM",
        duration: "30 mins",
        description: "3-mile easy recovery run booked for 7:00am."
      },
      {
        title: "Weekend Long Run",
        dayName: "Sat",
        dayNumber: "12",
        monthName: "July",
        time: "7:00 AM",
        duration: "90 mins",
        description: "10-mile long run booked for 7:00am."
      },
      {
        title: "Rest Day",
        dayName: "Sun",
        dayNumber: "13",
        monthName: "July",
        time: "Rest",
        duration: "All day",
        description: "Complete rest or light stretching."
      },
    ],
    [
      {
        title: "Rest Day",
        dayName: "Mon",
        dayNumber: "7",
        monthName: "July",
        time: "Rest",
        duration: "All day",
        description: "Complete rest or light stretching."
      },
      {
        title: "Rest Day",
        dayName: "Tue",
        dayNumber: "8",
        monthName: "July",
        time: "Rest",
        duration: "All day",
        description: "Complete rest or light stretching."
      },
      {
        title: "Rest Day",
        dayName: "Wed",
        dayNumber: "9",
        monthName: "July",
        time: "Rest",
        duration: "All day",
        description: "Complete rest or light stretching."
      },
      {
        title: "Tempo Run",
        dayName: "Thu",
        dayNumber: "10",
        monthName: "July",
        time: "6:00 AM",
        duration: "40 mins",
        description: "5-mile tempo run moved to Thursday at 6:00am."
      },
      {
        title: "Easy Recovery Run",
        dayName: "Fri",
        dayNumber: "11",
        monthName: "July",
        time: "7:00 AM",
        duration: "30 mins",
        description: "3-mile easy recovery run booked for 7:00am."
      },
      {
        title: "Weekend Long Run",
        dayName: "Sat",
        dayNumber: "12",
        monthName: "July",
        time: "7:00 AM",
        duration: "90 mins",
        description: "10-mile long run booked for 7:00am."
      },
      {
        title: "Rest Day",
        dayName: "Sun",
        dayNumber: "13",
        monthName: "July",
        time: "Rest",
        duration: "All day",
        description: "Complete rest or light stretching."
      },
    ],
    [
      {
        title: "Q2 Strategic Planning",
        startDate: "Mon Apr 1",
        endDate: "Tue Apr 2",
        time: "9:00 AM",
        duration: "2 days",
        description: "• Cross-departmental objectives\n• Risk Analysis\n• Q2 priorities finalization"
      },
      {
        title: "Market Expansion Strategy",
        startDate: "Thu May 16",
        endDate: "Thu May 16",
        time: "11:00 AM",
        duration: "1 day",
        description: "• Launch prep for EMEA\n• Team resource review"
      },
      {
        title: "Leadership Offsite - Strategic Initiatives",
        startDate: "Fri Jun 7",
        endDate: "Sat Jun 8",
        time: "All day",
        duration: "2 days",
        description: "• Executive alignment on 2025 plan\n• Speaker: Pat Lencioni"
      }
    ],
    [
      {
        title: "Beta Release v2.2",
        dayName: "Mon",
        dayNumber: "15",
        monthName: "April",
        description: "Critical release to selected partners"
      },
      {
        title: "Internal Q2 Roadmap Review",
        dayName: "Wed",
        dayNumber: "17",
        monthName: "April",
        description: "Stakeholder feedback session"
      },
      {
        title: "Public Announcement",
        dayName: "Fri",
        dayNumber: "26",
        monthName: "April",
        description: "Key PR milestone, update blog & socials"
      }
    ],
    [
      {
        title: "Closed $10M Series C",
        startDate: "Tue Feb 13",
        endDate: "Tue Feb 13",
        time: "2:00 PM",
        duration: "Day",
        description: "• Led negotiations\n• Legal team alignment"
      },
      {
        title: "Product of the Year - TechCrunch",
        startDate: "Fri Jun 21",
        endDate: "Fri Jun 21",
        time: "7:30 PM",
        duration: "Evening",
        description: "• Awards gala\n• Networking with press"
      },
      {
        title: "Launched Global Mentorship Program",
        startDate: "Mon Sep 9",
        endDate: "Mon Sep 9",
        time: "8:00 AM",
        duration: "Kickoff",
        description: "• Internal onboarding\n• Mentor-mentee matching"
      }
    ]
  ];

  const handleDemoChange = () => {
    if (isTyping) return;
    const nextIndex = (activeDemo + 1) % demoQueries.length;
    setIsTyping(true);
    setAnimatingEvents([]);
    const newQuery = demoQueries[nextIndex];
    setTypedText('');
    let charIndex = 0;
    const typeInterval = setInterval(() => {
      if (charIndex < newQuery.length) {
        setTypedText(newQuery.slice(0, charIndex + 1));
        charIndex++;
      } else {
        clearInterval(typeInterval);
        setTimeout(() => {
          setActiveDemo(nextIndex);
          const events = demoResults[nextIndex];
          events.forEach((_, index) => {
            setTimeout(() => {
              setAnimatingEvents(prev => [...prev, index]);
            }, index * 300);
          });
          setIsTyping(false);
        }, 500);
      }
    }, 50);
  };

  React.useEffect(() => {
    setActiveDemo(0);
    setTypedText(demoQueries[0]);
    const events = demoResults[0];
    setAnimatingEvents(events.map((_, index) => index));
    setIsTyping(false);
  }, []);

  return (
    <div className="mt-16 mb-32 max-w-2xl mx-auto px-4">
      <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 hover:shadow-3xl transition-all duration-500 hover:scale-[1.02] min-h-[1000px]">
        <div className="mb-8 space-y-4">
          <div className="flex items-center space-x-2 mb-4">
            <span className="font-medium text-gray-700 text-base">Try asking LinearCal:</span>
          </div>
          <div className="bg-gradient-to-r from-pink-100 to-teal-100 text-gray-800 border-2 border-pink-300 shadow-sm p-3 rounded-lg flex items-center justify-between">
            <span className="text-sm italic flex-1">
              "{typedText}"
            </span>
            <button
              onClick={handleDemoChange}
              className={`flex items-center justify-center w-12 h-12 rounded-full bg-white/60 hover:bg-white/80 transition-colors duration-200 ml-4 flex-shrink-0 ${
                isTyping ? 'opacity-50 cursor-not-allowed' : 'opacity-100 cursor-pointer hover:scale-105'
              }`}
              title={isTyping ? "Loading..." : "Try another example"}
              disabled={isTyping}
            >
              <span className={`text-gray-600 text-xl transition-transform duration-300 ${isTyping ? 'animate-spin' : ''}`}>⟳</span>
            </button>
          </div>
        </div>
        <div className="space-y-6">
          <div className="flex items-center space-x-2">
            <span className="font-medium text-gray-700 text-base">LinearCal Response:</span>
          </div>
          <div className="relative min-h-[320px]">
            <div className="space-y-4">
              {demoResults[activeDemo].map((event, index) => {
                const colClass = PALE_DOT_CLASSES[index % PALE_DOT_CLASSES.length];
                const isMarathonTraining = event.month && event.monthName;
                const isWeeklyTraining = event.dayName && event.dayNumber && !event.startHour;
                const isHourlyBooking = event.startHour && event.endHour;
                let dateDisplay;
                 if (isMarathonTraining) {
                  dateDisplay = (
                    <div className="bg-white rounded-xl p-2 sm:p-3 shadow-sm border border-gray-200 w-14 h-14 sm:w-24 sm:h-24 text-center relative z-10 flex flex-col justify-center">
                      <div className="text-[8px] sm:text-xs text-gray-600 font-medium leading-none">{event.month}</div>
                      <div className="text-xs sm:text-2xl font-bold text-gray-900 leading-none">{event.monthName}</div>
                      <div className="text-[8px] sm:text-xs text-gray-500 leading-none">{event.year}</div>
                    </div>
                  );
                } else if (isWeeklyTraining) {
                  dateDisplay = (
                    <div className="bg-white rounded-xl p-2 sm:p-3 shadow-sm border border-gray-200 w-14 h-14 sm:w-24 sm:h-24 text-center relative z-10 flex flex-col justify-center">
                      <div className="text-[8px] sm:text-xs text-gray-600 font-medium leading-none">{event.dayName}</div>
                      <div className="text-xs sm:text-2xl font-bold text-gray-900 leading-none">{event.dayNumber}</div>
                      <div className="text-[8px] sm:text-xs text-gray-500 leading-none">{event.monthName}</div>
                    </div>
                  );
                } else if (isHourlyBooking) {
                  dateDisplay = (
                    <>
                      <div className="bg-white rounded-xl p-2 sm:p-3 shadow-sm border border-gray-200 w-14 h-14 sm:w-24 sm:h-24 text-center relative z-10 flex flex-col justify-center">
                        <div className="text-[8px] sm:text-xs text-gray-500 leading-none">{event.dayName} {event.dayNumber}</div>
                        <div className="text-xs sm:text-2xl font-bold text-gray-900 leading-none">{event.startHour}</div>
                        <div className="text-[8px] sm:text-xs text-gray-500 leading-none">{event.monthName}</div>
                      </div>
                      <div className="w-0.5 h-4 bg-gray-300 -mt-1 -mb-1"></div>
                      <div className="bg-white rounded-xl p-2 sm:p-3 shadow-sm border border-gray-200 w-14 h-14 sm:w-24 sm:h-24 text-center relative z-10 flex flex-col justify-center">
                        <div className="text-[8px] sm:text-xs text-gray-500 leading-none">{event.dayName} {event.dayNumber}</div>
                        <div className="text-xs sm:text-2xl font-bold text-gray-900 leading-none">{event.endHour}</div>
                        <div className="text-[8px] sm:text-xs text-gray-500 leading-none">{event.monthName}</div>
                      </div>
                    </>
                  );
                } else {
                  const startDate = parseDate(event.startDate);
                  const endDate = parseDate(event.endDate);
                  dateDisplay = (
                    <>
                      <div className="bg-white rounded-xl p-2 sm:p-3 shadow-sm border border-gray-200 w-14 h-14 sm:w-24 sm:h-24 text-center relative z-10 flex flex-col justify-center">
                        <div className="text-[8px] sm:text-xs text-gray-600 font-medium leading-none">{startDate.month}</div>
                        <div className="text-xs sm:text-2xl font-bold text-gray-900 leading-none">{startDate.day}</div>
                        <div className="text-[8px] sm:text-xs text-gray-500 leading-none">July</div>
                      </div>
                      <div className="w-0.5 h-4 bg-gray-300 -mt-1 -mb-1"></div>
                      <div className="bg-white rounded-xl p-2 sm:p-3 shadow-sm border border-gray-200 w-14 h-14 sm:w-24 sm:h-24 text-center relative z-10 flex flex-col justify-center">
                        <div className="text-[8px] sm:text-xs text-gray-600 font-medium leading-none">{endDate.month}</div>
                        <div className="text-xs sm:text-2xl font-bold text-gray-900 leading-none">{endDate.day}</div>
                        <div className="text-[8px] sm:text-xs text-gray-500 leading-none">July</div>
                      </div>
                    </>
                  );
                }

                return (
                  <React.Fragment key={`${activeDemo}-${index}`}>
                    <div
                      className={`relative flex items-stretch space-x-4 group transition-all duration-500 hover:scale-[1.02] ${
                        animatingEvents.includes(index)
                          ? 'opacity-100 translate-y-0'
                          : 'opacity-0 translate-y-4'
                      }`}
                      style={{
                        transitionDelay: animatingEvents.includes(index) ? `${index * 200}ms` : '0ms',
                        transitionProperty: 'opacity, transform'
                      }}
                    >
                      <div className={`flex flex-col ${isWeeklyTraining ? 'items-center' : 'items-center justify-center'}`}>
                        {dateDisplay}
                      </div>
                      <div className={`flex-1 bg-white rounded-xl shadow-sm border border-gray-200 group-hover:shadow-md transition-all duration-300 group-hover:border-gray-300 flex flex-col ${
                        isWeeklyTraining ? 'p-3 min-h-24 justify-center' :
                        isHourlyBooking ? 'p-2 min-h-12' : 'p-4'
                      }`}>
                        <div className={`flex justify-between items-start mb-3`}>
                          <h3 className="font-semibold text-gray-900 leading-tight flex-1 text-base">{event.title}</h3>
                          <div className="ml-3 flex items-center space-x-2">
                            <div className="text-gray-400 cursor-pointer">
                              <Edit3 className="w-4 h-4" />
                            </div>
                          </div>
                        </div>
                        {event.description && !event.description.includes('•') && (
                          <div className="text-gray-600 leading-relaxed text-sm mb-3">
                              <p>{event.description}</p>
                          </div>
                        )}
                        {event.description && event.description.includes('•') && (
                          <div className="text-gray-600 leading-relaxed text-sm mb-3">
                            <p className="mb-2">{event.description.split('\n')[0].replace('•', '').trim()}</p>
                            <ul className="list-disc list-inside space-y-1 ml-2">
                              {event.description.split('\n').slice(1).map((item, idx) => (
                                <li key={idx}>{item.replace('•', '').trim()}</li>
                              ))}
                            </ul>
                            </div>
                        )}
                        {isHourlyBooking && event.title === "Dinner at Sarah's" && (
                          <div className="flex items-center justify-between mt-auto">
                            <div className="flex items-center space-x-2">
                            <div className="flex items-center space-x-1 bg-gray-100 rounded-lg px-2 py-1">
                              <span className="text-xs text-gray-700">Sarah Holerston</span>
                              <button
                                className="text-gray-400 hover:text-gray-600 text-xs focus:outline-none focus:ring-0 bg-transparent shadow-none p-0"
                                title="sarah.holerston@gmail.com"
                                style={{background: 'none', boxShadow: 'none'}}>
                                ×
                              </button>
                            </div>
                          </div>
                            <div className="flex items-center space-x-2">
                              <button className="bg-red-100 text-red-700 px-3 py-1 rounded text-xs hover:bg-red-200 transition-colors">
                                Reject
                              </button>
                              <button className="bg-green-100 text-green-700 px-3 py-1 rounded text-xs hover:bg-green-200 transition-colors">
                                Accept
                              </button>
                      </div>
                          </div>
                        )}
                        {isHourlyBooking && demoQueries[activeDemo] === "Show me tomorrow's agenda" && event.attendees && (
                          <div className="flex items-end justify-between w-full mt-auto">
                            <div className="flex items-center flex-wrap gap-1">
                              {event.attendees.map((name, i) => (
                                <span key={i} className="text-xs rounded bg-gray-100 px-2 py-0.5 text-gray-700 font-medium">{name}</span>
                              ))}
                            </div>
                            <span className="text-green-700 bg-green-100 px-2 py-0.5 rounded text-xs font-semibold ml-4">Accepted</span>
      </div>
                        )}
                        {isHourlyBooking && !(demoQueries[activeDemo] === "Show me tomorrow's agenda" && event.attendees) && event.title !== "Dinner at Sarah's" && (
                          <div className="flex items-center space-x-2 mt-auto">
                            <div className="flex items-center space-x-1 bg-gray-100 rounded-lg px-2 py-1">
                              <span className="text-xs text-gray-700">Sarah Holerston</span>
                              <button
                                className="text-gray-400 hover:text-gray-600 text-xs focus:outline-none focus:ring-0 bg-transparent shadow-none p-0"
                                title="sarah.holerston@gmail.com"
                                style={{background: 'none', boxShadow: 'none'}}>
                                ×
                              </button>
                          </div>
      </div>
                        )}
                      </div>
                    </div>
                    {index < demoResults[activeDemo].length - 1 && (
                      <div 
                        className={`my-4 transition-all duration-500 ${
                          animatingEvents.includes(index + 1)
                            ? 'opacity-100 scale-x-100'
                            : 'opacity-0 scale-x-0'
                        }`}
                        style={{
                          transitionDelay: animatingEvents.includes(index + 1) ? `${(index + 1) * 200}ms` : '0ms',
                          transformOrigin: 'left'
                        }}
                      >
                        <div className="border-b border-gray-200 w-full"></div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineDemo;